/**
 * LA FICHE D'UNE SURVEILLANCE : LA SURVEILLANCE, SON ALARME OUVERTE, SES ALARMES.
 * ============================================================================
 *
 * INTENTION : le client ouvre une surveillance et lit ce qu'elle surveille,
 * ses seuils, si elle suit les horaires du site, et ce qui lui est arrivé.
 *
 * La façade n'a pas de route pour une seule surveillance : elle se lit dans
 * la liste de son équipement (`GET /instance?objet_type=…&_id_objet=…`),
 * page après page jusqu'à la trouver. Une liste lue en entier sans elle dit
 * « introuvable » ; une lecture arrêtée avant la fin (plafond de pages vides)
 * ne le dit jamais : on ne sait pas, et la fiche échoue.
 *
 * Deux lectures d'alarmes, toutes deux par la LISTE (`GET /occurrence`,
 * jamais le détail, qui clôt une alarme revenue à la normale et non vue,
 * D71) :
 *  • l'alarme ouverte (`_id_instance` et `ouverte=true`), lue en entier : sans
 *    elle, la situation se tait plutôt que de se dire calme ;
 *  • l'historique (`_id_instance`), plafonné : au-delà, la fiche dit que la
 *    liste n'est pas complète, jamais qu'il n'y a rien d'autre.
 *
 * Tenu par session (personne et entreprise) et par surveillance : une fin de
 * lecture qui n'est plus la courante est jetée sans toucher à l'état.
 *
 * Lot F6 : après une écriture, la fiche est RELUE (`relire`) sans être vidée
 * d'abord : l'écran garde ce qu'il montrait jusqu'à la réponse, puis montre
 * ce que le serveur rend. Une relecture qui échoue garde la fiche et le dit
 * (rendu `false`) : l'écran ne propose pas de recommencer un geste incertain.
 */
import { defineStore } from "pinia"
import { ref } from "vue"
import { cleDeSession } from "@modules/alarmes-v2/stores/ouverture.store"
import {
    LIMITE_PAGE,
    PLAFOND_PAGES_VIDES,
} from "@modules/alarmes-v2/stores/surveillances.store"
import { serviceAlarmesV2 } from "@modules/alarmes-v2/services/alarmes-v2.service"
import {
    CODE_INDISPONIBLE,
    ErreurAlarmesV2,
    versErreurAlarmesV2,
} from "@modules/alarmes-v2/services/appel"
import type {
    AlarmesV2Alarme,
    AlarmesV2Personne,
    AlarmesV2Surveillance,
} from "@modules/alarmes-v2/types/alarmes-v2"

/** Le plafond de pages de l'historique (200 alarmes par page). [PROPOSITION] */
export const PLAFOND_PAGES_HISTORIQUE = 10

/** Le plafond de pages des alarmes ouvertes, garde contre une boucle sans fin. */
const PLAFOND_PAGES_OUVERTES = 5

/** Où en est la lecture de la fiche. */
export type StatutFiche =
    | "attente"
    | "chargement"
    | "pret"
    | "introuvable"
    | "erreur"

/** Où en est la lecture de l'historique. */
export type StatutHistorique = "attente" | "chargement" | "pret" | "erreur"

/** La surveillance visée. */
export interface SurveillanceVisee {
    objetType: "source" | "building"
    idObjet: string
    idInstance: string
}

class LectureObsolete extends Error {}

const illisible = () =>
    new ErreurAlarmesV2({ code: CODE_INDISPONIBLE, statut: null })

const cleDe = (session: string, v: SurveillanceVisee) =>
    session ? `${session}|${v.objetType}|${v.idObjet}|${v.idInstance}` : ""

export const useFicheStore = defineStore("alarmesV2Fiche", () => {
    /** La surveillance lue, `null` tant qu'elle n'est pas trouvée. */
    const surveillance = ref<AlarmesV2Surveillance | null>(null)

    /** Son alarme ouverte, `null` s'il n'y en a pas (lecture faite en entier). */
    const alarmeOuverte = ref<AlarmesV2Alarme | null>(null)

    /** Ses alarmes, de la plus ancienne à la plus récente. */
    const historique = ref<AlarmesV2Alarme[]>([])

    /** Vrai quand l'historique s'est arrêté au plafond : la liste n'est pas complète. */
    const historiqueIncomplet = ref(false)

    /** Où en est l'historique, lu après la fiche. */
    const statutHistorique = ref<StatutHistorique>("attente")

    /** Les noms servis avec la surveillance et ses alarmes. */
    const personnes = ref<Record<string, AlarmesV2Personne>>({})

    const statut = ref<StatutFiche>("attente")
    const erreur = ref<ErreurAlarmesV2 | null>(null)
    const cleLue = ref("")

    /** Vrai pendant une relecture après une écriture (la fiche reste montrée). */
    const relecture = ref(false)

    let lectureCourante = 0

    /** La surveillance de la dernière lecture, et si l'historique y était lu. */
    let viseeLue: SurveillanceVisee | null = null
    let historiqueDemande = false

    function vider(): void {
        surveillance.value = null
        alarmeOuverte.value = null
        historique.value = []
        historiqueIncomplet.value = false
        statutHistorique.value = "attente"
        personnes.value = {}
        erreur.value = null
    }

    const estCourante = (cle: string, numero: number, v: SurveillanceVisee) =>
        numero === lectureCourante && cleDe(cleDeSession(), v) === cle

    function verifier(cle: string, numero: number, v: SurveillanceVisee) {
        if (!estCourante(cle, numero, v)) throw new LectureObsolete()
    }

    /** Cherche la surveillance dans la liste de son équipement. */
    async function trouver(
        cle: string,
        numero: number,
        v: SurveillanceVisee
    ): Promise<AlarmesV2Surveillance | null> {
        let suivant: string | null = null
        let videsDeSuite = 0
        do {
            const page = await serviceAlarmesV2.alarmesV2ListerSurveillances({
                objet_type: v.objetType,
                _id_objet: v.idObjet,
                limite: LIMITE_PAGE,
                ...(suivant ? { curseur: suivant } : {}),
            })
            verifier(cle, numero, v)
            if (!Array.isArray(page?.items)) throw illisible()
            const trouvee = page.items.find((s) => s?._id === v.idInstance)
            if (trouvee) {
                personnes.value = {
                    ...personnes.value,
                    ...(page.personnes ?? {}),
                }
                return trouvee
            }
            videsDeSuite = page.items.length === 0 ? videsDeSuite + 1 : 0
            suivant = page.curseur_suivant ?? null
        } while (suivant && videsDeSuite < PLAFOND_PAGES_VIDES)
        // Arrêtée avant la fin : on ne sait pas, jamais « introuvable ».
        if (suivant) throw illisible()
        return null
    }

    /** Lit l'alarme ouverte de la surveillance, en entier. */
    async function lireLOuverte(
        cle: string,
        numero: number,
        v: SurveillanceVisee
    ): Promise<AlarmesV2Alarme | null> {
        let suivant: string | null = null
        let pages = 0
        let premiere: AlarmesV2Alarme | null = null
        do {
            const page = await serviceAlarmesV2.alarmesV2ListerAlarmes({
                _id_instance: v.idInstance,
                ouverte: true,
                limite: LIMITE_PAGE,
                ...(suivant ? { curseur: suivant } : {}),
            })
            verifier(cle, numero, v)
            if (!Array.isArray(page?.items)) throw illisible()
            premiere = premiere ?? page.items[0] ?? null
            personnes.value = { ...personnes.value, ...(page.personnes ?? {}) }
            suivant = page.curseur_suivant ?? null
            pages += 1
        } while (suivant && !premiere && pages < PLAFOND_PAGES_OUVERTES)
        if (!premiere && suivant) throw illisible()
        return premiere
    }

    /** Lit l'historique, plafonné ; une panne ne fait pas tomber la fiche. */
    async function lireLHistorique(
        cle: string,
        numero: number,
        v: SurveillanceVisee
    ): Promise<void> {
        statutHistorique.value = "chargement"
        const lues: AlarmesV2Alarme[] = []
        try {
            let suivant: string | null = null
            let pages = 0
            do {
                const page = await serviceAlarmesV2.alarmesV2ListerAlarmes({
                    _id_instance: v.idInstance,
                    limite: LIMITE_PAGE,
                    ...(suivant ? { curseur: suivant } : {}),
                })
                verifier(cle, numero, v)
                if (!Array.isArray(page?.items)) throw illisible()
                lues.push(...page.items)
                personnes.value = {
                    ...personnes.value,
                    ...(page.personnes ?? {}),
                }
                suivant = page.curseur_suivant ?? null
                pages += 1
            } while (suivant && pages < PLAFOND_PAGES_HISTORIQUE)
            historique.value = lues
            historiqueIncomplet.value = suivant !== null
            statutHistorique.value = "pret"
        } catch (e) {
            if (e instanceof LectureObsolete || !estCourante(cle, numero, v))
                return
            historique.value = []
            historiqueIncomplet.value = false
            statutHistorique.value = "erreur"
        }
    }

    /**
     * Lit la fiche, toujours relue (entrée sur la page). Ne rejette jamais.
     * @param v - la surveillance visée
     * @param options.historique - lire aussi l'historique (la fiche ; pas le détail)
     */
    async function charger(
        v: SurveillanceVisee,
        options: { historique?: boolean } = {}
    ): Promise<void> {
        const cle = cleDe(cleDeSession(), v)
        if (!cle || !v.idObjet || !v.idInstance) {
            reinitialiser()
            return
        }
        lectureCourante += 1
        const numero = lectureCourante
        vider()
        relecture.value = false
        viseeLue = { ...v }
        historiqueDemande = options.historique === true
        statut.value = "chargement"
        try {
            const trouvee = await trouver(cle, numero, v)
            if (!trouvee) {
                verifier(cle, numero, v)
                statut.value = "introuvable"
                cleLue.value = cle
                return
            }
            const ouverte = await lireLOuverte(cle, numero, v)
            verifier(cle, numero, v)
            surveillance.value = trouvee
            alarmeOuverte.value = ouverte
            statut.value = "pret"
            cleLue.value = cle
        } catch (e) {
            if (e instanceof LectureObsolete || !estCourante(cle, numero, v))
                return
            vider()
            erreur.value =
                e instanceof ErreurAlarmesV2 ? e : versErreurAlarmesV2(e)
            statut.value = "erreur"
            cleLue.value = cle
            return
        }
        if (options.historique) await lireLHistorique(cle, numero, v)
    }

    /**
     * Relit la surveillance montrée, après une écriture, SANS la vider
     * d'abord. Rend vrai quand la relecture a abouti (la surveillance relue,
     * ou introuvable : elle n'est plus visible) ; faux si elle a échoué, si la
     * session ou la surveillance montrée a changé. Ne rejette jamais.
     * @param v - la surveillance que l'écriture visait
     */
    async function relire(v: SurveillanceVisee): Promise<boolean> {
        const lue = viseeLue
        if (
            !lue ||
            lue.objetType !== v.objetType ||
            lue.idObjet !== v.idObjet ||
            lue.idInstance !== v.idInstance
        )
            return false
        const cle = cleDe(cleDeSession(), v)
        if (!cle || cle !== cleLue.value) return false
        if (statut.value !== "pret") {
            await charger(v, { historique: historiqueDemande })
            // Relu après l'attente : `charger` a changé le statut.
            const apres = statut.value as StatutFiche
            return ["pret", "introuvable"].includes(apres)
        }
        lectureCourante += 1
        const numero = lectureCourante
        relecture.value = true
        try {
            const trouvee = await trouver(cle, numero, v)
            if (!trouvee) {
                verifier(cle, numero, v)
                vider()
                statut.value = "introuvable"
                relecture.value = false
                return true
            }
            const ouverte = await lireLOuverte(cle, numero, v)
            verifier(cle, numero, v)
            surveillance.value = trouvee
            alarmeOuverte.value = ouverte
            relecture.value = false
        } catch (e) {
            if (e instanceof LectureObsolete || !estCourante(cle, numero, v))
                return false
            relecture.value = false
            return false
        }
        if (historiqueDemande) await lireLHistorique(cle, numero, v)
        return estCourante(cle, numero, v)
    }

    /** Remet le store à son état de départ ; toute lecture en vol est jetée. */
    function reinitialiser(): void {
        vider()
        lectureCourante += 1
        statut.value = "attente"
        cleLue.value = ""
        relecture.value = false
        viseeLue = null
        historiqueDemande = false
    }

    return {
        surveillance,
        alarmeOuverte,
        historique,
        historiqueIncomplet,
        statutHistorique,
        personnes,
        statut,
        erreur,
        cleLue,
        relecture,
        charger,
        relire,
        reinitialiser,
    }
})
